import axios from "axios";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8001").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;
export const API_BASE = API;
const ACCESS_TOKEN_STORAGE_KEY = "trailpulse:access_token";
const REFRESH_TOKEN_STORAGE_KEY = "trailpulse:refresh_token";

export const api = axios.create({ baseURL: API, withCredentials: true });

function storageGet(key) {
  try { return window.localStorage.getItem(key); } catch { return null; }
}

function storageSet(key, value) {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {}
}

export function setActiveOrg(orgId) {
  if (orgId) api.defaults.headers.common["X-Org-Id"] = orgId;
  else delete api.defaults.headers.common["X-Org-Id"];
}
let tokenGeneration = 0;
export function setToken(tok) {
  tokenGeneration += 1;
  storageSet(ACCESS_TOKEN_STORAGE_KEY, tok);
  if (tok) api.defaults.headers.common["Authorization"] = `Bearer ${tok}`;
  else delete api.defaults.headers.common["Authorization"];
}
export function setRefreshToken(tok) {
  storageSet(REFRESH_TOKEN_STORAGE_KEY, tok);
}

export function setAuthTokens(accessToken, refreshToken) {
  setToken(accessToken);
  if (refreshToken) setRefreshToken(refreshToken);
}

setToken(storageGet(ACCESS_TOKEN_STORAGE_KEY));

let refreshPromise = null;

api.interceptors.request.use((config) => {
  if (typeof window !== "undefined" && /^\/(?:my\/trips\/|trips\/|packing-items\/|ai\/my\/)/.test(config.url || "")) {
    const participantId = new URLSearchParams(window.location.search).get("participant_id");
    if (participantId) config.params = { participant_id: participantId, ...config.params };
  }
  return config;
});

export async function getAllPages(path, params = {}) {
  const first = await api.get(path, { params: { ...params, page: 1, page_size: 100 } });
  const rows = [...(first.data.data || [])];
  for (let page = 2; page <= (first.data.pagination?.pages || 1); page += 1) {
    const response = await api.get(path, { params: { ...params, page, page_size: 100 } });
    rows.push(...(response.data.data || []));
  }
  return { ...first, data: { ...first.data, data: rows } };
}

async function refreshAccessToken() {
  if (!refreshPromise) {
    const generation = tokenGeneration;
    const refreshToken = storageGet(REFRESH_TOKEN_STORAGE_KEY);
    refreshPromise = axios.post(`${API}/auth/refresh`, null, {
      withCredentials: true,
      headers: refreshToken ? { Authorization: `Bearer ${refreshToken}` } : {},
    }).then(({ data }) => {
      if (generation !== tokenGeneration) throw new Error("Your session changed. Please try again.");
      setToken(data.access_token);
      return data.access_token;
    }).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status === 401 && detail === "token_expired" && !original._retry) {
      original._retry = true;
      const access = await refreshAccessToken();
      original.headers = { ...(original.headers || {}), Authorization: `Bearer ${access}` };
      return api(original);
    }
    return Promise.reject(error);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Unable to reach the server. Check that the backend is running.";
  if (typeof detail === "string") {
    const key = detail.replace(/^\d{3}:\s*/, "");
    if (key.startsWith("trip_delete_blocked:")) return "Cancel active bookings and resolve refunds first. Trips with financial history must be retained.";
    if (key.startsWith("trip_commercial_edit_blocked:")) return key.includes("returning to draft")
      ? "This trip has active bookings or unsettled refunds. Make it private to stop discovery while retaining participant access."
      : "Booked dates and commercial terms cannot be changed. Resolve bookings and refunds first.";
    if (key.startsWith("document_requirement_changed:")) return "This document requirement has changed. Refresh your documents before signing.";
    const labels = {
      anywhere_requires_flexible_month: "Choose a travel month for Anywhere searches.",
      anywhere_requires_trip_duration: "Choose Weekend, One week or Two weeks for Anywhere searches.",
      invalid_trip_duration: "Choose Weekend, One week or Two weeks.",
      month_required: "Choose a travel month.",
      anywhere_month_out_of_range: "Choose a month within the next six months for Anywhere searches.",
      traveller_only_launch: "Only traveller accounts are available during the Flight Deals launch. Please sign in with a traveller account.",
      feature_not_available: "This feature is not available yet.",
      ai_not_configured: "AI is not configured. Add a valid OpenAI API key on the backend.",
      ai_timeout: "AI research was interrupted before it finished. Retry after checking the backend/provider connection.",
      ai_rate_limited: "OpenAI rate limit reached. Try again in a minute.",
      ai_credit_balance_exhausted: "OpenAI API credit balance is exhausted. Add API credits in OpenAI billing, then try again.",
      ai_quota_exceeded: "OpenAI quota or billing limit reached. Check the OpenAI project billing settings.",
      ai_auth_error: "OpenAI rejected the API key. Check the backend OpenAI key.",
      ai_model_unavailable: "The configured OpenAI model is unavailable for this API key. Update OPENAI_MODEL_NAME.",
      ai_model_parameter_unsupported: "The configured OpenAI model rejected one of the request settings.",
      ai_provider_error: "OpenAI could not create the draft. Check the backend logs for the provider error.",
      ai_preflight_requires_llm: "AI preflight now requires the configured OpenAI model so season and route choices are researched, not guessed.",
      ai_preflight_missing_ideal_months: "OpenAI could not add clear month guidance for this destination. You can retry, or continue with the trip details you already have.",
      ai_preflight_failed: "OpenAI could not research this trip idea. Retry or check the backend logs for the provider error.",
      token_expired: "Your session was refreshed. Please try again.",
      unauthenticated: "Please sign in again.",
      invalid_credentials: "Incorrect email/phone or password.",
      account_disabled: "This account has been deactivated. Contact your organizer or support.",
      account_not_found: "Account doesn't exist. Create an account.",
      invalid_or_expired_link: "This verification link has expired or was replaced. Request a new link and use the newest message.",
      registration_replaced: "A newer signup request replaced this one. Use the newest verification message.",
      email_exists: "An account already uses this email. Sign in or reset your password.",
      incorrect_password: "Enter your current password to update your contact details.",
      email_already_in_use: "Another account already uses this email address.",
      phone_already_in_use: "Another account already uses this phone number.",
      email_verification_unavailable: "Email verification is unavailable. Please try again later.",
      phone_verification_unavailable: "Phone verification is unavailable. Please try again later.",
      invalid_or_expired_verification: "This verification link is expired, already used, or belongs to another account. Request a new link from Settings.",
      phone_exists: "An account already uses this phone number. Sign in or reset your password.",
      sms_provider_not_configured: "Phone verification is unavailable. Please use email verification or email account recovery.",
      sms_send_failed: "The verification SMS could not be sent. Please try again or use email.",
      firebase_not_configured: "Phone verification is unavailable. Please use email.",
      firebase_verification_unavailable: "Phone verification could not be checked. Please try again.",
      firebase_proof_required: "Request a new phone code before verifying.",
      invalid_or_expired_otp: "Incorrect or expired code. Please try again or request a new code.",
      invalid_phone: "Enter a valid phone number with country code.",
      phone_mismatch: "This phone number does not match your account.",
      trip_payment_provider_not_configured: "Online trip payments are not available yet. Contact the organizer about payment.",
      booking_busy_retry: "Another booking is being processed. Please try again.",
      participant_email_verification_required: "Verify your account email before joining this existing reservation, or use your invitation link.",
      participant_owned_by_another_account: "This reservation belongs to another account. Contact your organizer.",
      individual_booking_required: "Create a separate booking for each traveller.",
      reservation_cannot_be_renewed: "This reservation cannot be renewed. Create a new enquiry or contact the organizer.",
      reservation_already_joined: "The traveller has already joined this reservation.",
      document_expired: "This document has expired. Submit a current document before continuing.",
      consent_text_required_from_organizer: "The organizer needs to add the consent terms before you can sign.",
      phone_and_emergency_contact_required: "Add your phone and an emergency contact before continuing.",
      dated_logistics_require_batch: "Select a departure for these transport or accommodation dates.",
      publish_requires_upcoming_departure_dates: "Add valid upcoming departure dates before publishing.",
      publish_requires_itinerary_activities: "Add itinerary activities before publishing.",
    };
    return labels[key] || detail;
  }
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatApiError(err, fallback = "Something went wrong.") {
  const status = err?.response?.status;
  if (!err?.response) return "Unable to reach the server. Check that the backend is running.";
  if (status === 401) return "Please sign in again.";
  if (status === 403) return "You do not have permission to do that.";
  if (status === 404) return "That item could not be found or is no longer available.";
  const detail = err?.response?.data?.detail;
  return detail == null ? fallback : formatApiErrorDetail(detail);
}

export function formatAuthError(err) {
  const detail = err?.response?.data?.detail;
  if (detail != null) return formatApiErrorDetail(detail);
  if (err?.isAxiosError || err?.request || err?.response) {
    return formatApiError(err, "The account request failed. Please try again.");
  }
  return err?.message || "The account request failed. Please try again.";
}

export function isPermissionError(err) {
  return [401, 403].includes(err?.response?.status);
}

export function confirmAction(message) {
  if (typeof window === "undefined" || typeof window.confirm !== "function") return true;
  return window.confirm(message);
}

// Download a CSV export from an authenticated GET endpoint (sends auth + X-Org-Id
// via the axios instance, then triggers a browser download from the blob).
export async function downloadCsv(path, filename) {
  const resp = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// Server-Sent Events reader for AI streaming endpoints (keeps the connection
// warm past the ingress idle timeout while the model generates).
export async function streamAi(path, body, { onStatus, onProgress, onDone, onError } = {}) {
  let resp;
  try {
    resp = await fetch(`${API}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(api.defaults.headers.common["Authorization"] ? { Authorization: api.defaults.headers.common["Authorization"] } : {}),
        ...(api.defaults.headers.common["X-Org-Id"] ? { "X-Org-Id": api.defaults.headers.common["X-Org-Id"] } : {}),
      },
      credentials: "include",
      body: JSON.stringify(body),
    });
  } catch (e) {
    onError?.("network_error");
    return;
  }
  if (!resp.ok || !resp.body) {
    let detail = "ai_error";
    try { detail = (await resp.json()).detail; } catch {}
    onError?.(detail);
    return;
  }
  const reader = resp.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const raw = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const ev = raw.match(/^event: (.*)$/m)?.[1];
      const dataLine = raw.match(/^data: (.*)$/m)?.[1];
      if (!dataLine) continue;
      let data;
      try { data = JSON.parse(dataLine); } catch { continue; }
      if (ev === "status") onStatus?.(data);
      else if (ev === "progress") onProgress?.(data);
      else if (ev === "done") onDone?.(data);
      else if (ev === "error") onError?.(data.detail);
    }
  }
}
